/**
 * Addon Service
 * API calls for add-on (single template download) purchase flow.
 */

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:5000';

/**
 * Create a Razorpay order for add-on purchase
 * @param {object} params
 * @param {string} params.userId
 * @param {number} params.amount       - Amount in paise (Rs.20 = 2000)
 * @param {string} params.templateId   - Template being purchased
 * @param {string} params.templateTier - 'silver' | 'gold' | 'platinum'
 * @param {string} params.limitType    - 'download' | 'ats' | 'coverLetter'
 * @param {string} params.addonId      - Firestore addon plan doc ID
 * @returns {Promise<{success: boolean, orderId: string, amount: number, currency: string, keyId: string}>}
 */
export const createAddonOrder = async ({ userId, amount, templateId, templateTier, limitType, addonId }) => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/payment/create-addon-order`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId,
        amount,
        templateId,
        templateTier,
        limitType,
        addonId
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { success: false, error: errorData.error || `Server error (${response.status})` };
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('createAddonOrder error:', error);
    return { success: false, error: 'Network error. Please try again.' };
  }
};

/**
 * Verify add-on payment and increment user limit
 * @param {object} params
 * @param {string} params.razorpay_order_id
 * @param {string} params.razorpay_payment_id
 * @param {string} params.razorpay_signature
 * @param {string} params.userId
 * @param {string} params.limitType    - 'download' | 'ats' | 'coverLetter'
 * @param {string} params.templateId
 * @param {string} params.templateTier
 * @param {string} params.addonId
 * @returns {Promise<{success: boolean, message: string, newLimit: number}>}
 */
export const verifyAddonPayment = async ({
  razorpay_order_id,
  razorpay_payment_id,
  razorpay_signature,
  userId,
  limitType,
  templateId,
  templateTier,
  addonId
}) => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/payment/verify-addon`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        razorpay_order_id,
        razorpay_payment_id,
        razorpay_signature,
        userId,
        limitType,
        templateId,
        templateTier,
        addonId
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { success: false, error: errorData.error || `Verification failed (${response.status})` };
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('verifyAddonPayment error:', error);
    return { success: false, error: 'Network error during verification.' };
  }
};

/**
 * Fetch addon plan details from backend
 * @returns {Promise<{success: boolean, data: object}>}
 */
export const getAddonPlanDetails = async () => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/payment/addon-plan`);
    if (!response.ok) {
      return { success: false, error: 'Failed to fetch addon plan' };
    }
    const data = await response.json();
    return data;
  } catch (error) {
    return { success: false, error: 'Network error' };
  }
};

export default {
  createAddonOrder,
  verifyAddonPayment,
  getAddonPlanDetails
};
